import {
  KnockFeedProvider,
  KnockProvider as KnockRoot,
  NotificationFeedPopover,
  NotificationIconButton,
  useKnockFeed,
} from "@knocklabs/react";
import { type ReactNode, useRef, useState, useEffect } from "react";
import { useAuth } from "../../lib/authContext";
import { useToast } from "../../hooks/use-toast";
import { useNavigate } from "react-router-dom";

import "@knocklabs/react/dist/index.css";

interface KnockWrapperProps {
  children: ReactNode;
}

// Separate component to handle notifications inside KnockFeedProvider
const NotificationHandler = () => {
  const { feedClient } = useKnockFeed();
  const { toast } = useToast();
  const navigate = useNavigate();
  const lastNotificationId = useRef<string | null>(null);

  useEffect(() => {
    // Request browser notification permission
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }

    // Listen for real-time notifications
    const handleNewNotification = (data: any) => {
      const items = data?.items || [];

      // Take only the first item
      const item = items[0];
      if (!item) return;

      // Only show notification if source key is "order-on-hold"
      if (item.source?.key !== "order-on-hold") {
        return;
      }

      // Skip if we've already processed this notification
      if (lastNotificationId.current === item.id) {
        return;
      }
      lastNotificationId.current = item.id;

      // Extract blocks
      const bodyBlock = item.blocks?.find((b: any) => b.name === "body");
      const actionUrlBlock = item.blocks?.find(
        (b: any) => b.name === "action_url",
      );

      // Fallbacks
      const title = "Order On Hold";
      const message = `Order ${item.data?.invoice_number} has moved to on hold!`;
      const actionUrl = `${process.env.REACT_APP_ORDER_VIEW_URL}${item.data?.order_id}`;

      // Show toast notification
      toast({
        title,
        description: (
          <div className="flex flex-col gap-2">
            {/* Render message content safely */}
            <div className="text-sm">{message}</div>
            <button
              type="button"
              onClick={() => window.open(actionUrl, "_blank")}
              className="mt-2 text-blue-600 hover:text-blue-800 underline text-sm font-medium"
            >
              View Details
            </button>
          </div>
        ),
        duration: 6000,
      });
    };

    // Subscribe to real-time events
    feedClient.on("items.received.realtime", handleNewNotification);

    // Cleanup
    return () => {
      feedClient.off("items.received.realtime", handleNewNotification);
    };
  }, [feedClient, toast, navigate]);

  return null;
};

export const KnockWrapper = () => {
  const { user } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const notifButtonRef = useRef(null);

  return (
    <KnockRoot
      apiKey={process.env.REACT_APP_KNOCK_PUBLIC_API_KEY ?? ""}
      userId={user?.id ?? ""}
    >
      <KnockFeedProvider
        feedId={process.env.REACT_APP_KNOCK_FEED_CHANNEL_ID ?? ""}
      >
        <>
          <NotificationHandler />
          <NotificationIconButton
            ref={notifButtonRef}
            onClick={() => setIsVisible(!isVisible)}
          />
          <NotificationFeedPopover
            buttonRef={notifButtonRef}
            isVisible={isVisible}
            onClose={() => setIsVisible(false)}
          />
        </>
      </KnockFeedProvider>
    </KnockRoot>
  );
};
